import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllEntityData } from '../api/entity_load_api';

// In-memory cache for ongoing entity data requests to prevent duplicates
const ongoingRequests = new Map();

export const useEntitySearch = () => {
  const [searchEntityId, setSearchEntityId] = useState('');
  const [searchFailed, setSearchFailed] = useState(false);

  const onSearchChange = useCallback((value) => {
    setSearchEntityId(value);
    setSearchFailed(false);
  }, []);

  const validateSearchInput = useCallback(() => {
    // Allow empty searches - they will just clear the flexible_text filter
    return true;
  }, []);

  const setSearchFailure = useCallback(() => {
    setSearchFailed(true);
  }, []);

  return {
    searchEntityId,
    searchFailed,
    onSearchChange,
    validateSearchInput,
    setSearchFailure,
  };
};


/**
 * Custom hook for managing entity data and operations
 */
export const useEntityData = (entityId) => {
  const [entityData, setEntityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const previousEntityIdRef = useRef(null);
  const abortControllerRef = useRef(null);

  const loadEntityData = useCallback(async () => {
    if (!entityId) {
      setEntityData(null);
      setError(null);
      setLoading(false);
      previousEntityIdRef.current = null;
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    // If entity ID changed, cancel previous request and clear old data
    if (previousEntityIdRef.current !== entityId && previousEntityIdRef.current !== null) {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Clean up any ongoing request for the previous entity
      const previousRequestKey = `entity_${previousEntityIdRef.current}`;
      if (ongoingRequests.has(previousRequestKey)) {
        ongoingRequests.delete(previousRequestKey);
      }
      // Clean up loading state for previous entity
      loadingRef.current = false;
      setEntityData(null);
      setError(null);
    }
    previousEntityIdRef.current = entityId;

    // Check if there's already an ongoing request for this entity
    const requestKey = `entity_${entityId}`;
    if (ongoingRequests.has(requestKey)) {
      try {
        const data = await ongoingRequests.get(requestKey);
        // Only update if request wasn't aborted and entity ID hasn't changed
        if (!abortControllerRef.current?.signal.aborted && previousEntityIdRef.current === entityId) {
          setEntityData(data);
          setLoading(false);
          loadingRef.current = false;
          // Clean up the request from the Map after successful use
          ongoingRequests.delete(requestKey);
        } else {
          // Request was aborted or entity changed, clean up
          ongoingRequests.delete(requestKey);
        }
        return;
      } catch (err) {
        // If the ongoing request failed, remove it and continue to make a new one
        ongoingRequests.delete(requestKey);
        if (err.name === 'AbortError') {
          // Request was aborted, reset loading state and don't make new request if entity changed
          if (previousEntityIdRef.current !== entityId) {
            setLoading(false);
            loadingRef.current = false;
            return;
          }
          // If same entity, continue to make a new request
        }
      }
    }

    // Prevent duplicate requests for the same entity
    // Only skip if we're already loading the SAME entity
    if (loadingRef.current && previousEntityIdRef.current === entityId) {
      return;
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Create and store the promise for this request
      // Pass signal for cancellation support
      const requestPromise = getAllEntityData(entityId, signal);
      ongoingRequests.set(requestKey, requestPromise);
      
      const data = await requestPromise;
      
      // Only update state if request wasn't aborted and entity ID hasn't changed
      if (!signal.aborted && previousEntityIdRef.current === entityId) {
        setEntityData(data);
      }
      // Always clean up the request from the Map
      ongoingRequests.delete(requestKey);
    } catch (err) {
      // Clean up the request from ongoingRequests regardless of error type
      ongoingRequests.delete(requestKey);
      
      // Ignore abort errors
      if (err.name === 'AbortError' || signal.aborted) {
        // Reset loading state even for aborted requests
        if (previousEntityIdRef.current === entityId) {
          setLoading(false);
          loadingRef.current = false;
        }
        return;
      }
      
      // Only update error state if request wasn't aborted and entity ID hasn't changed
      if (!signal.aborted && previousEntityIdRef.current === entityId) {
        setEntityData(null);
        setError(err.message);
        setLoading(false);
        loadingRef.current = false;
      }
    } finally {
      // Always reset loading state if this is still the current entity
      if (previousEntityIdRef.current === entityId) {
        if (!signal.aborted) {
          setLoading(false);
          loadingRef.current = false;
        }
      }
      // Clean up abort controller if this is still the current one
      if (abortControllerRef.current?.signal === signal) {
        abortControllerRef.current = null;
      }
    }
  }, [entityId]);

  // Cleanup: cancel request on unmount or entityId change
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [entityId]);

  // Update operations have been removed - application is now read-only


  useEffect(() => {
    loadEntityData();
  }, [loadEntityData, entityId]);

  return {
    entityData,
    loading,
    error
  };
};

